import { useEffect, useState } from 'react';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminRole, AdminUser, AdminUserStatus } from '@mds/shared/types/admin';

import * as admin from '@/administration/administration.api';
import { ADMIN_STATUSES } from '@/administration/status-tone';
import RolePicker from '@/components/administration/role-picker';
import Button from '@/components/ui/button';
import Field, { inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import { PASSWORD_MIN_LENGTH, adminUserSchema, newAdminUserSchema } from '@/domain/administrators';
import { useValidation } from '@/lib/validation';

/** Blank fields are sent as null, which is how the API clears them. */
const orNull = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

interface Draft {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: AdminUserStatus;
  password: string;
  roleIds: string[];
}

const blank: Draft = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  status: 'active',
  password: '',
  roleIds: [],
};

/**
 * Creates an administrator, or edits the fields the PATCH contract accepts.
 *
 * Roles are set here only on create — the API replaces them through a separate
 * endpoint, so an existing administrator changes theirs on the record instead.
 */
export default function AdminUserEditor({
  open,
  user,
  roles,
  isSelf,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null when creating. */
  user: AdminUser | null;
  roles: AdminRole[];
  isSelf: boolean;
  onClose: () => void;
  onSaved: (saved: AdminUser) => void | Promise<void>;
}) {
  const [form, setForm] = useState<Draft>(blank);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const validation = useValidation(user ? adminUserSchema : newAdminUserSchema, form);
  const { reset } = validation;

  useEffect(() => {
    if (!open) return;
    setError(null);
    reset();
    setForm(
      user
        ? {
            email: user.email,
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            phone: user.phone ?? '',
            status: user.status,
            password: '',
            roleIds: user.roles.map((r) => r.id),
          }
        : blank,
    );
  }, [open, user, reset]);

  const save = async () => {
    if (!validation.check()) return;

    setBusy(true);
    setError(null);
    try {
      const saved = user
        ? await admin.updateUser(user.id, {
            email: form.email.trim(),
            firstName: orNull(form.firstName),
            lastName: orNull(form.lastName),
            phone: orNull(form.phone),
            status: form.status,
          })
        : await admin.createUser({
            email: form.email.trim(),
            firstName: orNull(form.firstName),
            lastName: orNull(form.lastName),
            phone: orNull(form.phone),
            status: form.status,
            password: form.password,
            roleIds: form.roleIds,
          });
      await onSaved(saved);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={user ? 'Edit administrator' : 'Add administrator'}
      footer={
        <>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : user ? 'Save changes' : 'Create administrator'}
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" {...validation.field('firstName')}>
          <input
            className={inputCls}
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
        </Field>
        <Field label="Last name" {...validation.field('lastName')}>
          <input
            className={inputCls}
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </Field>
        <Field
          label="Email"
          required
          hint={user ? 'Changing this signs them out everywhere.' : undefined}
          {...validation.field('email')}
        >
          <input
            type="email"
            className={inputCls}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Phone" {...validation.field('phone')}>
          <input
            type="tel"
            className={inputCls}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
        <Field
          label="Status"
          required
          hint={
            isSelf
              ? 'You cannot suspend or deactivate your own account.'
              : 'Anything other than active signs them out.'
          }
          {...validation.field('status')}
        >
          <Select
            variant="field"
            className={selectCls}
            value={form.status}
            onChange={(status) => setForm({ ...form, status: status as AdminUserStatus })}
            options={ADMIN_STATUSES.map((s) => ({ value: s, label: s }))}
            disabled={isSelf}
            aria-label="Status"
          />
        </Field>
      </div>

      {!user && (
        <>
          <Field
            label="Password"
            required
            hint={`At least ${PASSWORD_MIN_LENGTH} characters. Send it to them through an approved secure channel, never by plain email.`}
            {...validation.field('password')}
          >
            <input
              type="password"
              className={inputCls}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
            />
          </Field>

          <RolePicker
            label="Roles"
            hint="At least one is required."
            roles={roles}
            value={form.roleIds}
            onChange={(roleIds) => setForm({ ...form, roleIds })}
            {...validation.field('roleIds', { within: true })}
          />
        </>
      )}

      {error && (
        <p
          role="alert"
          className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
        >
          {error}
        </p>
      )}
    </Modal>
  );
}
